/**
 * @module smwebsdk
 */

/*
 * Copyright 2017-2020 Soul Machines Ltd. All Rights Reserved.
 */

/**
 * Features class
 *
 * An instance of this class can also be created by the convenience API function DetectCapabilities()
 *
 * ```ts
 * var features  = window.smwebsdk.DetectCapabilities();
 * ```
 *
 * which also calls the detectWebRTCFeatures() method.
 */
export class Features {
  private _hasMicrophone = false;
  private _hasCamera = false;
  private _isAndroid = false;
  private _isBrowserSupported = false;
  private _isEdge = false;
  private _isIos = false;

  constructor() {
    this._isAndroid = this.detectAndroid();
    this._isEdge = this.detectEdge();
    this._isIos = this.detectIos();
  }

  private detectEdge(): boolean {
    return this.userAgentMatches('Edge');
  }

  private detectAndroid(): boolean {
    return this.userAgentMatches('Android');
  }

  private detectIos(): boolean {
    const isIOS =
      (/iPad|iPhone|iPod/i.test(navigator.platform) ||
        (navigator &&
          navigator.platform === 'MacIntel' &&
          navigator.maxTouchPoints > 1)) &&
      !window.MSStream;
    return isIOS;
  }

  private userAgentMatches(text: string): boolean {
    const userAgent = window.navigator.userAgent;
    const matches: RegExpMatchArray | null = userAgent.match(text);
    if (matches) {
      return matches.length > 0 ? true : false;
    }
    return false;
  }

  public detectWebRTCFeatures(): Promise<Features> {
    return new Promise(
      (resolve: (value: any) => void, reject: (value: any) => void) => {
        // Check for runtime host
        if (window.SmIsUnderRuntimeHost) {
          // TODO - detect whether camera/microphone is available in runtime host
          this._isBrowserSupported = true;
          this._hasMicrophone = true;
          this._hasCamera = true;
          resolve(this);
        }

        // This approach heavily influenced by the helpful and MIT licensed:
        //    https://github.com/muaz-khan/DetectRTC/blob/master/DetectRTC.js

        // Detect webrtc support
        let isWebRTCSupported = false;
        [
          'RTCPeerConnection',
          'webkitRTCPeerConnection',
          'mozRTCPeerConnection',
          'RTCIceGatherer',
        ].forEach((item) => {
          if (isWebRTCSupported) {
            return;
          }

          if (item in window) {
            isWebRTCSupported = true;
          }
        });

        // Apparently IE11 injected iPhone into user agent string, hence check for not MSStream
        const iOS =
          navigator.userAgent.match(/iPhone|iPad|iPod/i) && !window.MSStream;
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent
        );
        if (isWebRTCSupported && iOS && !isSafari) {
          // Webrtc is not supported on an iOS device using webview instead of full Safari
          isWebRTCSupported = false;
        }

        this._isBrowserSupported = isWebRTCSupported;

        if (!this._isBrowserSupported) {
          resolve(this);
        }

        // Find the enumerate devices function if available
        let navEnumerateDevices = null;
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
          // Firefox 38+ seems having support of enumerateDevices
          // Thanks @xdumaine/enumerateDevices
          // eslint-disable-next-line @typescript-eslint/ban-types
          navEnumerateDevices = (callback: Function) => {
            Promise.resolve(navigator.mediaDevices.enumerateDevices())
              .then((value: MediaDeviceInfo[] = []) => {
                callback(value);
              })
              .catch(() => {
                callback([]);
              });
          };
        }

        const { MediaStreamTrack } = window;
        if (
          !navEnumerateDevices &&
          MediaStreamTrack &&
          MediaStreamTrack.getSources
        ) {
          navEnumerateDevices =
            MediaStreamTrack.getSources.bind(MediaStreamTrack);
        }

        if (!navEnumerateDevices && navigator.enumerateDevices) {
          // ! block was originally unreachable until bug is fixed in trunk
          navEnumerateDevices = navigator.enumerateDevices.bind(navigator);
        }

        // Enumerate the media devices
        if (navEnumerateDevices) {
          navEnumerateDevices((devices: MediaDeviceInfo[]) => {
            devices.forEach((device: MediaDeviceInfo) => {
              if (device.kind === 'audioinput') {
                this._hasMicrophone = true;
              }
              if (device.kind === 'videoinput') {
                this._hasCamera = true;
              }
            });

            resolve(this);
          });
        } else {
          resolve(this);
        }
      }
    );
  }

  get hasMicrophone(): boolean {
    return this._hasMicrophone;
  }

  get hasCamera(): boolean {
    return this._hasCamera;
  }

  get isAndroid(): boolean {
    return this._isAndroid;
  }

  get isBrowserSupported(): boolean {
    return this._isBrowserSupported;
  }

  get isEdge(): boolean {
    return this._isEdge;
  }

  get isIos(): boolean {
    return this._isIos;
  }
}
